import React from 'react';
import {Typography, Steps} from 'antd';
import {CheckOutlined} from '@ant-design/icons';
import {StepStatus} from '../../core/materialui/Stepper';
import {MODEL_UPLOAD_STEPS} from './types';
import './ModelUploadHeaderRedesigned.module.scss';

const {Title, Paragraph} = Typography;

type ModelUploadHeaderRedesignedProps = {
  activeStep: number;
  setActiveStep: (index: number) => void;
  getStepStatus: (index: number) => StepStatus;
};

const ModelUploadHeaderRedesigned: React.FC<ModelUploadHeaderRedesignedProps> = ({
  activeStep,
  setActiveStep,
  getStepStatus,
}) => {
  // Map 5 backend steps to 4 UI steps:
  // FILES (0) -> UI step 0
  // METADATA (1) + LINEAGE (2) -> UI step 1 (combined as "Metadata")
  // PERMISSIONS (3) -> UI step 2
  // NAME_CONFIRMATION (4) -> UI step 3 (shown as "Review")

  const stepConfig = [
    {
      title: 'Files',
      backendStep: MODEL_UPLOAD_STEPS.FILES,
      onClick: () => setActiveStep(MODEL_UPLOAD_STEPS.FILES),
    },
    {
      title: 'Metadata',
      backendStep: MODEL_UPLOAD_STEPS.METADATA,
      onClick: () => setActiveStep(MODEL_UPLOAD_STEPS.METADATA),
      // This UI step represents both METADATA (1) and LINEAGE (2)
      includesLineage: true,
    },
    {
      title: 'Permissions',
      backendStep: MODEL_UPLOAD_STEPS.PERMISSIONS,
      onClick: () => setActiveStep(MODEL_UPLOAD_STEPS.PERMISSIONS),
    },
    {
      title: 'Review',
      backendStep: MODEL_UPLOAD_STEPS.NAME_CONFIRMATION,
      onClick: () => setActiveStep(MODEL_UPLOAD_STEPS.NAME_CONFIRMATION),
    },
  ];

  // Determine which UI step should be active based on backend activeStep
  const getCurrentUIStep = () => {
    if (activeStep === MODEL_UPLOAD_STEPS.FILES) return 0;
    if (activeStep === MODEL_UPLOAD_STEPS.METADATA || activeStep === MODEL_UPLOAD_STEPS.LINEAGE) return 1;
    if (activeStep === MODEL_UPLOAD_STEPS.PERMISSIONS) return 2;
    if (activeStep === MODEL_UPLOAD_STEPS.NAME_CONFIRMATION) return 3;
    return 0;
  };

  const currentUIStep = getCurrentUIStep();

  // Get the status for each UI step based on backend step statuses
  const getUIStepStatus = (uiStepIndex: number) => {
    const step = stepConfig[uiStepIndex];
    const backendStatus = getStepStatus(step.backendStep);

    // For Metadata step which includes both METADATA and LINEAGE, check both
    if (step.includesLineage) {
      const lineageStatus = getStepStatus(MODEL_UPLOAD_STEPS.LINEAGE);
      // If either METADATA or LINEAGE has an error/warning, show that
      if (backendStatus === StepStatus.ERROR || lineageStatus === StepStatus.ERROR) {
        return StepStatus.ERROR;
      }
      if (backendStatus === StepStatus.WARNING || lineageStatus === StepStatus.WARNING) {
        return StepStatus.WARNING;
      }
      // Both must be COMPLETED for Metadata to be COMPLETED
      if (backendStatus === StepStatus.COMPLETED && lineageStatus === StepStatus.COMPLETED) {
        return StepStatus.COMPLETED;
      }
      return StepStatus.DEFAULT;
    }

    return backendStatus;
  };

  // Determine if a step should show as finished (with checkmark)
  const isStepFinished = (uiStepIndex: number) => {
    const status = getUIStepStatus(uiStepIndex);
    // Only show checkmark if step is completed successfully (COMPLETED status)
    // and we're past that step
    return uiStepIndex < currentUIStep && status === StepStatus.COMPLETED;
  };

  // Map backend StepStatus to Ant Design step status
  const getAntdStepStatus = (uiStepIndex: number) => {
    if (uiStepIndex === currentUIStep) {
      return 'process';
    }

    const status = getUIStepStatus(uiStepIndex);
    if (uiStepIndex < currentUIStep && status === StepStatus.COMPLETED) {
      return 'finish';
    }

    if (status === StepStatus.ERROR) {
      return 'error';
    }

    return 'wait';
  };

  return (
    <div style={{borderBottom: '1px solid #e8e8e8', padding: '32px 80px 24px', background: '#ffffff'}}>
      <div style={{maxWidth: '1400px', margin: '0 auto'}}>
        <Title level={1} style={{fontSize: '36px', fontWeight: 400, marginBottom: '8px', color: '#000'}}>
          Add Model Entry
        </Title>
        <Paragraph style={{fontSize: '14px', color: '#595959', marginBottom: '32px'}}>
          Add model files, documentation and metadata
        </Paragraph>

        <Steps
          current={currentUIStep}
          items={stepConfig.map((step, index) => ({
            title: step.title,
            status: getAntdStepStatus(index) as any,
            icon: isStepFinished(index) ? <CheckOutlined /> : undefined,
          }))}
          onChange={(current) => {
            // Map UI step click back to backend step
            stepConfig[current].onClick();
          }}
          style={{
            fontSize: '16px',
          }}
          className="model-upload-steps"
        />
      </div>
    </div>
  );
};

export default ModelUploadHeaderRedesigned;
